import React, { useEffect, useMemo, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./assetBookingMember.css";
import { Alert, Badge, Button, Card, Col, Form, ListGroup, Row, Spinner } from "react-bootstrap";
import {
    createAssetBooking,
    getAssetBookingCalendar,
    getAssets,
    getMyAssetBookings,
} from "../../api/requests";

const statusLabel = {
    pending: "Ausstehend",
    approved: "Freigegeben",
    rejected: "Abgelehnt",
};

const statusVariant = {
    pending: "warning",
    approved: "success",
    rejected: "danger",
};

const toDateTimeLocalValue = (date) => {
    if (!date) return "";
    const pad = (value) => String(value).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const timePartsFromLocalValue = (value, fallbackHour, fallbackMinute = 0) => {
    if (!value || !value.includes("T")) {
        return { hours: fallbackHour, minutes: fallbackMinute };
    }
    const [, time = ""] = value.split("T");
    const [hoursRaw, minutesRaw] = time.split(":");
    const hours = Number.parseInt(hoursRaw, 10);
    const minutes = Number.parseInt(minutesRaw, 10);
    return {
        hours: Number.isNaN(hours) ? fallbackHour : hours,
        minutes: Number.isNaN(minutes) ? fallbackMinute : minutes,
    };
};

const withTimeFromLocalValue = (date, value, fallbackHour) => {
    const next = new Date(date);
    const { hours, minutes } = timePartsFromLocalValue(value, fallbackHour);
    next.setHours(hours, minutes, 0, 0);
    return next;
};

const monthRange = (date) => {
    const start = new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 1, 0, 0, 0, 0);
    return { start, end };
};

const bookingTouchesDate = (booking, date) => {
    const start = new Date(booking.start_datetime);
    const end = new Date(booking.end_datetime);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
    const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
    const dayEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1, 0, 0, 0, 0);
    return start < dayEnd && end > dayStart;
};

const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return "";
    const d = new Date(dateTimeStr);
    if (Number.isNaN(d.getTime())) return String(dateTimeStr);
    return d.toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatMonth = (date) => (
    date.toLocaleDateString("de-DE", {
        month: "long",
        year: "numeric",
    })
);

const errorText = (error) => {
    const data = error?.response?.data;
    if (!data) return "Aktion fehlgeschlagen.";
    if (typeof data.detail === "string") return data.detail;
    if (Array.isArray(data.non_field_errors)) return data.non_field_errors.join(" ");
    if (Array.isArray(data?.non_field_errors?.[0])) return data.non_field_errors[0].join(" ");
    return "Bitte prüfe die Eingaben.";
};

const assetColor = (assetId) => {
    const colors = ["#0b6bcb", "#0f8b6f", "#c25a1e", "#7c3aed", "#b91c1c", "#2563eb"];
    const value = Number(assetId) || 0;
    return colors[value % colors.length];
};

const AssetBookingMember = () => {
    const [assets, setAssets] = useState([]);
    const [calendarDate, setCalendarDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [calendarBookings, setCalendarBookings] = useState([]);
    const [myBookings, setMyBookings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const defaultStart = useMemo(() => {
        const date = new Date();
        date.setHours(10, 0, 0, 0);
        return toDateTimeLocalValue(date);
    }, []);

    const defaultEnd = useMemo(() => {
        const date = new Date();
        date.setHours(18, 0, 0, 0);
        return toDateTimeLocalValue(date);
    }, []);

    const [form, setForm] = useState({
        asset: "",
        start_datetime: defaultStart,
        end_datetime: defaultEnd,
        purpose: "",
    });

    const selectedAsset = useMemo(
        () => assets.find((asset) => String(asset.id) === String(form.asset)),
        [assets, form.asset],
    );

    const selectedRange = useMemo(() => {
        const start = form.start_datetime ? new Date(form.start_datetime) : selectedDate;
        const end = form.end_datetime ? new Date(form.end_datetime) : selectedDate;
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return selectedDate;
        return [start, end];
    }, [form.start_datetime, form.end_datetime, selectedDate]);

    const load = async (date = calendarDate) => {
        setLoading(true);
        setError(null);
        try {
            const range = monthRange(date);
            const [assetItems, calendarItems, ownItems] = await Promise.all([
                getAssets({ active: true }),
                getAssetBookingCalendar({ start: range.start.toISOString(), end: range.end.toISOString() }),
                getMyAssetBookings(),
            ]);
            const activeAssets = Array.isArray(assetItems) ? assetItems.filter((asset) => asset.is_active !== false) : [];
            setAssets(activeAssets);
            setCalendarBookings(Array.isArray(calendarItems) ? calendarItems : []);
            setMyBookings(Array.isArray(ownItems) ? ownItems : []);
            setForm((prev) => ({
                ...prev,
                asset: activeAssets.some((asset) => String(asset.id) === String(prev.asset))
                    ? prev.asset
                    : (activeAssets[0]?.id ? String(activeAssets[0].id) : ""),
            }));
        } catch (e) {
            console.error(e);
            setError(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load(calendarDate);
    }, [calendarDate]);

    const monthBookings = useMemo(
        () => [...calendarBookings].sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime)),
        [calendarBookings],
    );

    const handleDateChange = (value) => {
        const range = Array.isArray(value) ? value : [value, value];
        const startDate = range[0] || new Date();
        const endDate = range[1] || startDate;
        const start = withTimeFromLocalValue(startDate, form.start_datetime, 10);
        const end = withTimeFromLocalValue(endDate, form.end_datetime, 18);

        setSelectedDate(startDate);
        setForm((prev) => ({
            ...prev,
            start_datetime: toDateTimeLocalValue(start),
            end_datetime: toDateTimeLocalValue(end),
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            await createAssetBooking({
                asset: Number(form.asset),
                start_datetime: form.start_datetime,
                end_datetime: form.end_datetime,
                purpose: form.purpose.trim(),
            });
            setForm((prev) => ({ ...prev, purpose: "" }));
            await load(calendarDate);
        } catch (e) {
            console.error(e);
            setError(e);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Card className="asset-booking-card">
            <Card.Header>Materialverleih</Card.Header>
            <Card.Body>
                {error && (
                    <Alert variant="danger">
                        {errorText(error)}
                    </Alert>
                )}

                {loading ? (
                    <Spinner animation="border" size="sm" />
                ) : null}

                <Row className="g-3">
                    <Col xs={12} lg={5}>
                        <div className="asset-calendar-shell">
                            <Calendar
                                value={selectedRange}
                                onChange={handleDateChange}
                                selectRange
                                returnValue="range"
                                onActiveStartDateChange={({ activeStartDate }) => {
                                    if (activeStartDate) setCalendarDate(activeStartDate);
                                }}
                                prev2Label={null}
                                next2Label={null}
                                tileClassName={({ date, view }) => {
                                    if (view !== "month") return null;
                                    const count = calendarBookings.filter((booking) => bookingTouchesDate(booking, date)).length;
                                    return count ? "asset-calendar-booked-day" : null;
                                }}
                                tileContent={({ date, view }) => {
                                    if (view !== "month") return null;
                                    const dayBookings = calendarBookings.filter((booking) => bookingTouchesDate(booking, date));
                                    if (!dayBookings.length) return null;
                                    const visible = dayBookings.slice(0, 3);
                                    return (
                                        <div className="asset-calendar-dots" aria-label={`${dayBookings.length} Ausleihen`}>
                                            {visible.map((booking) => (
                                                <span
                                                    key={`${booking.id}-${booking.asset}`}
                                                    className="asset-calendar-dot"
                                                    style={{ backgroundColor: assetColor(booking.asset) }}
                                                />
                                            ))}
                                            {dayBookings.length > visible.length ? (
                                                <span className="asset-calendar-more">+{dayBookings.length - visible.length}</span>
                                            ) : null}
                                        </div>
                                    );
                                }}
                            />
                        </div>
                        <div className="asset-calendar-help">
                            Im Kalender Starttag und Endtag auswählen. Die Uhrzeiten kannst du rechts anpassen.
                        </div>

                        <div className="asset-day-panel">
                            <div className="asset-section-title">
                                Ausleihen im {formatMonth(calendarDate)}
                            </div>
                            {monthBookings.length === 0 ? (
                                <div className="asset-empty-state">Keine freigegebenen Ausleihen in diesem Monat.</div>
                            ) : (
                                <ListGroup className="asset-booking-list">
                                    {monthBookings.map((booking) => (
                                        <ListGroup.Item key={booking.id} className="asset-booking-list-item">
                                            <div className="asset-booking-heading">
                                                <span
                                                    className="asset-booking-swatch"
                                                    style={{ backgroundColor: assetColor(booking.asset) }}
                                                />
                                                <span>{booking.asset_name}</span>
                                            </div>
                                            <div className="asset-booking-time">
                                                {formatDateTime(booking.start_datetime)} bis {formatDateTime(booking.end_datetime)}
                                            </div>
                                            {booking.purpose ? <div className="asset-booking-purpose">{booking.purpose}</div> : null}
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            )}
                        </div>
                    </Col>

                    <Col xs={12} lg={7}>
                        <Form className="asset-booking-form" onSubmit={handleSubmit}>
                            <div className="asset-section-title">Neue Anfrage</div>
                            <Row className="g-2">
                                <Col xs={12}>
                                    <Form.Group>
                                        <Form.Label>Material</Form.Label>
                                        <Form.Select
                                            value={form.asset}
                                            onChange={(e) => setForm((prev) => ({ ...prev, asset: e.target.value }))}
                                            required
                                        >
                                            {assets.length === 0 ? (
                                                <option value="">Kein Material verfügbar</option>
                                            ) : (
                                                assets.map((asset) => (
                                                    <option key={asset.id} value={asset.id}>
                                                        {asset.name}
                                                    </option>
                                                ))
                                            )}
                                        </Form.Select>
                                        {selectedAsset?.description ? (
                                            <div className="asset-selected-description">
                                                {selectedAsset.description}
                                            </div>
                                        ) : null}
                                    </Form.Group>
                                </Col>
                                <Col xs={12} md={6}>
                                    <Form.Group>
                                        <Form.Label>Start</Form.Label>
                                        <Form.Control
                                            type="datetime-local"
                                            value={form.start_datetime}
                                            onChange={(e) => setForm((prev) => ({ ...prev, start_datetime: e.target.value }))}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={12} md={6}>
                                    <Form.Group>
                                        <Form.Label>Ende</Form.Label>
                                        <Form.Control
                                            type="datetime-local"
                                            value={form.end_datetime}
                                            onChange={(e) => setForm((prev) => ({ ...prev, end_datetime: e.target.value }))}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={12}>
                                    <Form.Group>
                                        <Form.Label>Zweck</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            value={form.purpose}
                                            onChange={(e) => setForm((prev) => ({ ...prev, purpose: e.target.value }))}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={12}>
                                    <Button type="submit" disabled={submitting || assets.length === 0}>
                                        {submitting ? "Sende..." : "Anfrage senden"}
                                    </Button>
                                </Col>
                            </Row>
                        </Form>

                        <div className="asset-my-bookings">
                            <div className="asset-section-title">Meine Anfragen</div>
                            {myBookings.length === 0 ? (
                                <div className="asset-empty-state">Noch keine Anfragen gestellt.</div>
                            ) : (
                                <ListGroup className="asset-booking-list">
                                    {myBookings.slice(0, 8).map((booking) => (
                                        <ListGroup.Item key={booking.id} className="asset-booking-list-item">
                                            <div className="asset-my-booking-row">
                                                <div>
                                                    <div className="asset-booking-heading">
                                                        <span
                                                            className="asset-booking-swatch"
                                                            style={{ backgroundColor: assetColor(booking.asset) }}
                                                        />
                                                        <span>{booking.asset_name}</span>
                                                    </div>
                                                    <div className="asset-booking-time">
                                                        {formatDateTime(booking.start_datetime)} bis {formatDateTime(booking.end_datetime)}
                                                    </div>
                                                    <div className="asset-booking-purpose">{booking.purpose}</div>
                                                    {booking.admin_note ? (
                                                        <div className="asset-booking-note">Notiz: {booking.admin_note}</div>
                                                    ) : null}
                                                </div>
                                                <Badge bg={statusVariant[booking.status] || "secondary"} text={booking.status === "pending" ? "dark" : undefined}>
                                                    {statusLabel[booking.status] || booking.status}
                                                </Badge>
                                            </div>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            )}
                        </div>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

export default AssetBookingMember;
